/* eslint-disable max-lines, yoda */
import {
    NOTIFICATION_TYPE_INFO,
    NOTIFICATION_TYPE_SUCCESS,
    NOTIFICATION_TYPE_WARN,
} from '../../../constants';
import { getCMSConfig, getPreMarketingAvcResult } from '../../../util/cms';
import { formatDownload, formatPrice, formatProductName, generateTxtContent } from '../../../util/formatter';
import { PreMarketingType, ProductType } from '../../../enums/product';
import { NO_PATTERN_BUSINESS_LOGIC } from '@vfde-brix/ws10/core';
import { getOptionPageUrl, getProductPageUrl } from '../../../util/url';
import { IButtonProperties } from '@vfde-brix/ws10/button';
import { INotificationProperties } from '@vfde-brix/ws10/notification';
import { NotificationState } from '@vfde-brix/ws10/notification-icon';
import { APOAvcResult } from '../../../interfaces/additionalPageOptions';
import { setOverlayVisiblity } from '../../../components/overlay';
import { AvcResult, JarvisProduct } from '@vfde-sails/avc-core';
import { getPreMarketingBtnLink } from '../../../util/components/buttonHelper';
import { FiberSupplierData } from '../interface';
import { GELB_GRAU, GRUEN, NVD, VFDE, VVM } from '../constants';
import { mapFiberWholeBuyConfig } from '../../../util/authApi/mappingHelper';

/**
 * Gets the tooltip config for the avc notification
 * if selected is available and there is regioFee
 * @param avcResult Result of the avc
 * @returns Tooltip config for avc notication
 */
export const getAvcNotificationTooltip = ({ selectedAvailable, selected }: AvcResult) => {
    const config = getCMSConfig()?.avcResult;

    if (!config) {
        return undefined;
    }

    return selectedAvailable && selected.regioCharge ? config.fiber.available.regioFee.tooltip : undefined;
};

/**
 * getAvcNotificationProps
 * Gets the props that need to be updated
 * on the notification component
 * @param avcResult Result of the avc
 * @returns Notification props to update
 */

export const getWholebuyAvcNotificationProps = (fiberSupplierData: FiberSupplierData | undefined,
    productName: string) :
    Partial<INotificationProperties> => {
    if (!fiberSupplierData) {return {};}
    const { availabilityIndicator, rolloutEndMonth, accessSupplier } = fiberSupplierData;
    const fiberStatus = availabilityIndicator === GELB_GRAU ? VVM : NVD;
    const cmsConfig = getCMSConfig();
    const currentCmsConfig = accessSupplier ? mapFiberWholeBuyConfig(cmsConfig, accessSupplier) : null;
    const subline = currentCmsConfig?.[fiberStatus]?.subline || '';
    const dateText = (rolloutEndMonth &&
    currentCmsConfig?.[fiberStatus]?.
        date_text?.replace('$date', `<strong>${rolloutEndMonth}</strong>`))
        || '';

    const overlayText = cmsConfig?.avcResult?.wholebuy?.[fiberStatus]?.text;
    const overlayHeadline = currentCmsConfig?.headline.replace('$product', productName || '');

    return {
        optState: NOTIFICATION_TYPE_SUCCESS,
        stdHeadline: overlayHeadline,
        txtContent: generateTxtContent(subline, dateText, overlayText),
    };
};
/**
 *
 * @param AvcResult
 * @param preMarketingType
 * @param fiberSupplierData
 * this function all depends on the avc result to show the notification after doing the availiblty check
 * it checks on the access supplier if it exists if it does exist the check if it is vfde continue the regular
 * flow otherwise check the availblity indicator with value gruen get the text based on the access supplier
 * text value
 * @returns
 */
export const getAvcNotificationProps = (
    { selectedAvailable, selected, fastest, availability }: AvcResult,
    preMarketingType?: PreMarketingType,
    fiberSupplierData?: FiberSupplierData,
): Partial<INotificationProperties> => {
    const config = getCMSConfig()?.avcResult;

    if (!config) {
        return {};
    }

    let state: NotificationState, headline, text, product;

    if (selectedAvailable) {
        state = NOTIFICATION_TYPE_SUCCESS;
        headline = config.fiber.available.text;
        text = config.fiber.available.fastest;
        product = selected;
    } else if (availability.fiber) {
        state = NOTIFICATION_TYPE_INFO;
        headline = config.fiber.slowerAvailable.text;
        text = config.fiber.slowerAvailable.slower;
        product = fastest;
    } else {
        state = NOTIFICATION_TYPE_WARN;
        headline = config.fiber.notAvailable.text;
        text = config.fiber.notAvailable.lmText;
        product = selected;
    }

    if (selectedAvailable && selected.regioCharge) {
        const regioFeeText = formatPrice(config.fiber.available.regioFee.text, selected.regioCharge);
        headline = [headline, regioFeeText].join(' ');
    }

    if (fiberSupplierData && fiberSupplierData.accessSupplier !== VFDE && selectedAvailable){

        if (GRUEN !== fiberSupplierData?.availabilityIndicator) {
            return getWholebuyAvcNotificationProps(fiberSupplierData, product?.name || '');
        }
    }

    if (preMarketingType) {
        const preMarketingConfig = getPreMarketingAvcResult(preMarketingType);

        return {
            optState: NOTIFICATION_TYPE_SUCCESS,
            stdHeadline: preMarketingConfig?.headline.replace('$product', product?.name || ''),
            txtContent: preMarketingConfig?.subline,
        };
    }

    return {
        optState: state,
        stdHeadline: formatProductName(headline, product?.name),
        txtContent: formatDownload(text, fastest?.download),
    };
};

const getFiberAvcResultBtnProps = (
    config: APOAvcResult,
    tariffId: string,
    isGigakombi: boolean,
    product: JarvisProduct,
): Partial<IButtonProperties> => {
    const props: Partial<IButtonProperties> = {};

    if (product.regioCharge) {
        // in case of regio fee available the result button have to
        // close the overlay to show the new price

        props.stdContent = config.fiber.available.regioFee.button;
        props.business = { onClick: () => setOverlayVisiblity(false) };
        props.linkHref = undefined;

        return props;
    }

    // redirect to option page
    props.stdContent = config.fiber.available.button;
    props.linkHref = getOptionPageUrl(tariffId, isGigakombi);

    return props;
};

export const getWholebuyButtonProps = (avcIndicator: string | undefined,
    accessSupplier: string | undefined) => {
    const cmsConfig = getCMSConfig();
    const currentCmsConfig = accessSupplier ? mapFiberWholeBuyConfig(cmsConfig, accessSupplier) : null;
    const fiberStatus = avcIndicator === GELB_GRAU ? VVM : NVD;
    return {
        business: NO_PATTERN_BUSINESS_LOGIC,
        stdContent: currentCmsConfig?.[fiberStatus]?.button?.text,
        linkHref: currentCmsConfig?.[fiberStatus]?.button?.link,
    };
};

/**
 * Gets the props that need to be
 * updated for the avc result button
 * @param tariffId - Id of the selected tariff
 * @param isGigakombi - Gigakombi chosen or not
 * @param product - Fastest available product
 */
export const getAvcResultButtonProps = (
    tariffId: string,
    isGigakombi: boolean,
    product?: JarvisProduct,
    preMarketingType?: PreMarketingType,
    fiberSupplierData?: FiberSupplierData,
): Partial<IButtonProperties> => {
    const config = getCMSConfig()?.avcResult as APOAvcResult;

    if (!config || preMarketingType === PreMarketingType.BHS) {
        return {};
    }

    if (fiberSupplierData && fiberSupplierData.accessSupplier !== VFDE) {
        if (GRUEN !== fiberSupplierData.availabilityIndicator) {
            return getWholebuyButtonProps(fiberSupplierData?.availabilityIndicator,
                fiberSupplierData?.accessSupplier);
        }
    }
    if (preMarketingType) {
        const preMarketingConfig = getPreMarketingAvcResult(preMarketingType);

        return {
            business: NO_PATTERN_BUSINESS_LOGIC,
            stdContent: preMarketingConfig?.button,
            linkHref: getPreMarketingBtnLink(preMarketingType),
        };
    }

    let props: Partial<IButtonProperties> = {
        business: NO_PATTERN_BUSINESS_LOGIC,
    };

    switch (product?.productType) {
        case ProductType.FIBRE:
            props = getFiberAvcResultBtnProps(config, tariffId, isGigakombi, product);
            break;
        case ProductType.CABLE:
            props.linkHref = getProductPageUrl(product.productType, product.id);
            props.stdContent = config.cable.button;
            break;
        case ProductType.DSL:
            props.linkHref = getProductPageUrl(product.productType, product.id);
            props.stdContent = config.dsl.button;
            break;
        default:
            props.linkHref = config.gigacube.link;
            props.stdContent = config.gigacube.button;
            break;
    }

    return props;
};

/**
 * getAvcResultAlternativeText
 * Gets the text to be shown for an alternative
 * product when fiber is not available
 * @param fastest fastest available tariff
 * @returns Formatted alternative text
 */
export const getAvcResultAlternativeText = (fastest?: JarvisProduct) => {
    const config = getCMSConfig()?.avcResult;

    if (!config) {
        return;
    }

    switch (fastest?.productType) {
        case ProductType.FIBRE:
            return;
        case ProductType.CABLE:
            return formatDownload(config.cable.text, fastest.download);
        case ProductType.DSL:
            return formatDownload(config.dsl.text, fastest.download);
        default:
            return config.gigacube.text;
    }
};
/**
 * getAvcResultAlternativeText
 * Gets the text to be shown for an alternative
 * product when fiber is not available
 * @param fastest fastest available tariff
 * @returns Formatted alternative text
 */
export const getAvcResultSecondAlternativeText = (fastest?: JarvisProduct) => {
    const config = getCMSConfig()?.avcResult;

    if (!config) {
        return;
    }

    switch (fastest?.productType) {
        case ProductType.CABLE:
            return formatDownload(config.cable.text, fastest.download);
        case ProductType.DSL:
            return formatDownload(config.dsl.text, fastest.download);
        default:
            return config.gigacube.text;
    }
};
/**
 * isPreSales
 * @param fiberSupplierData
 * @returns true if there is DTAG/DGW in the presales phase
 */
export const isPreSales = (fiberSupplierData: any) => {
    const notGG = fiberSupplierData.accessSupplier.accessSupplier !== VFDE;
    return notGG && (fiberSupplierData.availabilityIndicator === GELB_GRAU);
};
